// src/screens/PerfilScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebaseConfig';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import {
  IoArrowBack,
  IoCamera,
  IoTimeOutline,
  IoCloseCircleOutline,
  IoCheckmarkCircleOutline,
  IoPersonOutline,
  IoHomeOutline,
  IoStorefrontOutline,
  IoWalletOutline,
  IoSettingsOutline,
  IoKeyOutline,
  IoLogOutOutline,
  IoConstructOutline,
  IoReceiptOutline,
  IoCardOutline,
  IoChatbubblesOutline,
  IoCashOutline,
  IoPeopleOutline,
  IoDocumentTextOutline,
  IoShieldCheckmarkOutline
} from 'react-icons/io5';
import { APP_VERSION } from '../version';
import GestionInquilinosCard from '../components/Residentes/GestionInquilinosCard';
import BotonPanico from '../components/Safety/BotonPanico';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import { AVISO_PRIVACIDAD_TEXT, TERMINOS_CONDICIONES_TEXT } from '../utils/legalTexts';
import './PerfilScreen.css';

// Brand and custom avatar presets
import AvatarViewer, { BRAND_AVATARS, EMOJI_AVATARS } from '../components/Shared/AvatarViewer';

function PerfilScreen() {
  const navigate = useNavigate();
  const { currentUser, userData, refreshUserData, logout, isMoroso } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mensajeExito, setMensajeExito] = useState('');

  // Estados del formulario
  const [nombre, setNombre] = useState('');
  const [numeroCasa, setNumeroCasa] = useState('');
  const [numeroCelular, setNumeroCelular] = useState('');
  const [tipoResidente, setTipoResidente] = useState('Inquilino');
  
  // Estados para el Horario de Atención
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleStart, setScheduleStart] = useState('09:00');
  const [scheduleEnd, setScheduleEnd] = useState('18:00');
  const [scheduleDays, setScheduleDays] = useState([1, 2, 3, 4, 5]); // 1 = Lunes, 7 = Domingo
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyMessage, setAutoReplyMessage] = useState('Hola, nuestro horario de atención es de 9:00 AM a 6:00 PM de Lunes a Viernes. Te responderemos a la brevedad dentro de ese horario.');

  // Estados para la foto y modal
  const [fotoPreview, setFotoPreview] = useState(null);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [activeTab, setActiveTab] = useState('brand'); // 'brand' | 'emoji'

  const [condominioNombre, setCondominioNombre] = useState('');
  const [unidadesLista, setUnidadesLista] = useState([]);
  const [codigoActivacion, setCodigoActivacion] = useState('');
  const [loadingCondoData, setLoadingCondoData] = useState(true);

  // Estado del Proveedor
  const [providerStatus, setProviderStatus] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');

  // Estados para Cambiar Contraseña y PIN de Seguridad
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [securityPin, setSecurityPin] = useState('');
  const [activeSecurityTab, setActiveSecurityTab] = useState('password'); // 'password' | 'pin'

  // Estados para Modal de Documentos Legales
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalModalType, setLegalModalType] = useState('terms'); // 'terms' | 'privacy'

  // --- CHECK PROVIDER STATUS & SELF-HEAL ---
  const checkProviderStatus = async () => {
    const pId = userData.proveedorId || currentUser.uid;
    if (!pId) return;

    try {
      const docRef = doc(db, "market_proveedores", pId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const pData = docSnap.data();
        setProviderStatus(pData.estatus);
        if (pData.estatus === 'rechazado' && pData.motivoRechazo) {
          setMotivoRechazo(pData.motivoRechazo);
        }
        if (!pData.condominioId && userData.condominioId) {
          await updateDoc(docRef, { condominioId: userData.condominioId });
        }
      }
    } catch (err) {
      console.error("Error checking provider status:", err);
    }
  };

  useEffect(() => {
    if (userData) {
      setNombre(userData.nombre || '');
      setNumeroCasa(userData.numeroCasa || '');
      setNumeroCelular(userData.numeroCelular || '');
      setTipoResidente(userData.tipoResidente || 'Inquilino');
      setFotoPreview(userData.fotoPerfilUrl || null);
      
      // Cargar horario de atención
      if (userData.horarioAtencion) {
        setScheduleEnabled(userData.horarioAtencion.habilitado || false);
        setScheduleStart(userData.horarioAtencion.horaInicio || '09:00');
        setScheduleEnd(userData.horarioAtencion.horaFin || '18:00');
        setScheduleDays(userData.horarioAtencion.dias || [1, 2, 3, 4, 5]);
        setAutoReplyEnabled(userData.horarioAtencion.autoRespuestaActiva || false);
        setAutoReplyMessage(userData.horarioAtencion.mensajeAutoRespuesta || '');
      }

      const fetchCondominioData = async () => {
        if (!userData.condominioId) {
          setCondominioNombre('Proveedor Externo (Sin Condominio)');
          setLoadingCondoData(false);
          return;
        }
        try {
          setLoadingCondoData(true);
          const condoRef = doc(db, "condominios", userData.condominioId);
          const condoSnap = await getDoc(condoRef);

          if (condoSnap.exists()) {
            const condoData = condoSnap.data();
            setCondominioNombre(condoData.nombre || '');
            setUnidadesLista(condoData.unidades || []);
            setCodigoActivacion(condoData.codigoActivacion || '');
          } else {
            setError("Error: No se pudo cargar la información de tu condominio.");
          }
        } catch (e) {
          console.error("Error al cargar datos del condominio:", e);
          setError("Error al cargar configuración.");
        } finally {
          setLoadingCondoData(false);
        }
      };

      fetchCondominioData();
      checkProviderStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const handleFotoChange = async (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setLoading(true);
      setError('');
      setMensajeExito('');
      try {
        setFotoPreview(URL.createObjectURL(file));
        const storageRef = ref(storage, `usuarios/${currentUser.uid}/${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(snapshot.ref);

        const userDocRef = doc(db, "usuarios", currentUser.uid);
        await updateDoc(userDocRef, {
          fotoPerfilUrl: downloadUrl
        });

        await refreshUserData();
        setMensajeExito("¡Foto de perfil actualizada con éxito!");
        setShowAvatarModal(false);
      } catch (err) {
        console.error("Error al subir foto de perfil:", err);
        setError("Error al guardar la foto de perfil: " + (err.message || err));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSelectPresetAvatar = async (presetString) => {
    setLoading(true);
    setError('');
    setMensajeExito('');
    try {
      const userDocRef = doc(db, "usuarios", currentUser.uid);
      await updateDoc(userDocRef, {
        fotoPerfilUrl: presetString
      });
      await refreshUserData();
      setFotoPreview(presetString);
      setMensajeExito("¡Avatar personalizado guardado con éxito!");
      setShowAvatarModal(false);
    } catch (e) {
      console.error("Error al guardar avatar preset:", e);
      setError("Error al actualizar el avatar.");
    } finally {
      setLoading(false);
    }
  };




  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMensajeExito('');

    if (!nombre || !numeroCasa || !numeroCelular) {
      setError("Nombre, N° de Casa y Celular son obligatorios.");
      return;
    }

    setLoading(true);

    try {
      if (tipoResidente === 'Propietario') {
        const q = query(
          collection(db, "usuarios"),
          where("condominioId", "==", userData.condominioId),
          where("numeroCasa", "==", numeroCasa),
          where("tipoResidente", "==", "Propietario")
        );
        const querySnapshot = await getDocs(q);
        let propietarioExistente = null;
        querySnapshot.forEach(doc => {
          if (doc.id !== currentUser.uid) {
            propietarioExistente = doc.data();
          }
        });
        if (propietarioExistente) {
          setError(`Error: Ya existe un 'Propietario' (${propietarioExistente.nombre}) para la unidad ${numeroCasa}. Selecciona 'Inquilino'.`);
          setLoading(false);
          return;
        }
      }

      const userDocRef = doc(db, "usuarios", currentUser.uid);
      const isStaff = (userData?.rol === 'admin' || userData?.rol === 'comite' || userData?.rol === 'superuser');
      const updateData = {
        nombre: nombre,
        numeroCelular: numeroCelular
      };
      
      if (isStaff) {
        updateData.horarioAtencion = {
          habilitado: scheduleEnabled,
          horaInicio: scheduleStart,
          horaFin: scheduleEnd,
          dias: scheduleDays,
          autoRespuestaActiva: autoReplyEnabled,
          mensajeAutoRespuesta: autoReplyMessage
        };
      }
      
      await updateDoc(userDocRef, updateData);

      await refreshUserData();
      setMensajeExito("¡Perfil actualizado con éxito!");
      setLoading(false);

    } catch (e) {
      console.error("Error al guardar perfil:", e);
      setError("Error al guardar el perfil.");
      setLoading(false);
    }
  };

  const handleChangePasswordClick = () => {
    setError('');
    setMensajeExito('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setCurrentPin('');
    setSecurityPin('');
    setActiveSecurityTab('password');
    setShowPasswordModal(true);
  };

  const handleConfirmPasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setMensajeExito('');

    if (!currentPassword) {
      setError("Debes ingresar tu contraseña actual.");
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setError("La nueva contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setLoading(true);
    try {
      // Re-authenticate
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      
      // Update Password
      await updatePassword(currentUser, newPassword);
      setMensajeExito("¡Contraseña cambiada con éxito!");
      setShowPasswordModal(false);
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/wrong-password') {
        setError("La contraseña actual es incorrecta.");
      } else {
        setError("Error al cambiar la contraseña. Verifica tus datos o re-inicia sesión.");
      }
    }
    setLoading(false);
  };

  const handleUpdatePin = async (e) => {
    e.preventDefault();
    setError('');
    setMensajeExito('');

    // If they already have a PIN configured, require validating it
    const hasPinConfigured = !!userData?.securityPin;
    if (hasPinConfigured && currentPin !== userData.securityPin) {
      setError("El PIN actual es incorrecto.");
      return;
    }

    if (!/^\d{4}$/.test(securityPin)) {
      setError("El nuevo PIN debe tener exactamente 4 dígitos numéricos.");
      return;
    }

    setLoading(true);
    try {
      const userDocRef = doc(db, "usuarios", currentUser.uid);
      await updateDoc(userDocRef, { securityPin: securityPin });
      await refreshUserData();
      setMensajeExito("¡PIN de seguridad actualizado con éxito!");
      setShowPasswordModal(false);
    } catch (err) {
      console.error(err);
      setError("Error al guardar el PIN.");
    }
    setLoading(false);
  };

  const handleRestrictedAccess = (e) => {
    e.preventDefault();
    alert('Acceso denegado. Tienes adeudos pendientes.');
    navigate('/pagar-cuota');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error("Error al cerrar sesión", err);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmation = window.prompt(
      "ADVERTENCIA: Esta acción eliminará permanentemente tu cuenta de Kondify y toda tu información personal de nuestros servidores.\n\nPara confirmar la eliminación, escribe la palabra ELIMINAR:"
    );
    if (confirmation !== "ELIMINAR") {
      if (confirmation !== null) {
        alert("Confirmación incorrecta. No se ha eliminado la cuenta.");
      }
      return;
    }

    setLoading(true);
    setError('');
    setMensajeExito('');
    try {
      // 1. Delete firestore user doc
      const userDocRef = doc(db, "usuarios", currentUser.uid);
      await deleteDoc(userDocRef);

      // 2. Delete user from auth
      await currentUser.delete();

      // 3. Logout and redirect
      await logout();
      alert("Tu cuenta y datos han sido eliminados de forma permanente.");
      navigate('/login');
    } catch (err) {
      console.error("Error al eliminar cuenta:", err);
      if (err.code === 'auth/requires-recent-login') {
        setError("Por motivos de seguridad, debes cerrar sesión e iniciar sesión nuevamente antes de poder eliminar tu cuenta.");
        alert("Por seguridad, debes cerrar sesión e iniciar sesión nuevamente antes de poder eliminar tu cuenta.");
      } else {
        setError("Error al eliminar tu cuenta: " + (err.message || err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContactAdmin = async () => {
    if (!userData?.condominioId) {
      alert("No perteneces a ningún condominio activo.");
      return;
    }
    setLoading(true);
    try {
      const q = query(
        collection(db, "usuarios"),
        where("condominioId", "==", userData.condominioId),
        where("rol", "==", "admin")
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const adminId = querySnapshot.docs[0].id;
        navigate(`/chat/${adminId}`);
      } else {
        const qComite = query(
          collection(db, "usuarios"),
          where("condominioId", "==", userData.condominioId),
          where("rol", "==", "comite")
        );
        const snapComite = await getDocs(qComite);
        if (!snapComite.empty) {
          const comiteId = snapComite.docs[0].id;
          navigate(`/chat/${comiteId}`);
        } else {
          alert("No se encontró ningún Administrador registrado para tu condominio en este momento. Por favor contacta al comité físico.");
        }
      }
    } catch (err) {
      console.error("Error finding administrator:", err);
      alert("Ocurrió un error al contactar con la administración.");
    } finally {
      setLoading(false);
    }
  };

  const renderProviderSection = () => {
    if (providerStatus === 'en_revision' || providerStatus === 'aprobado_admin') {
      return (
        <div className="provider-status-card pending">
          <IoTimeOutline size={30} />
          <div className="provider-status-content">
            <strong>Solicitud en Revisión</strong>
            <span>Tu solicitud está siendo procesada, te notificaremos pronto.</span>
          </div>
        </div>
      );
    }

    if (providerStatus === 'rechazado') {
      return (
        <div className="provider-status-card rejected">
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '16px' }}>
            <IoCloseCircleOutline size={30} />
            <div className="provider-status-content">
              <strong>Solicitud Rechazada</strong>
              <span>{motivoRechazo || "Revisa tus documentos."}</span>
            </div>
          </div>
          <button className="provider-action-btn" onClick={() => navigate('/registro-proveedor')}>
            Corregir y Reenviar
          </button>
        </div>
      );
    }

    if (providerStatus === 'activo') {
      return (
        <div className="provider-status-card active">
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '16px' }}>
            <IoCheckmarkCircleOutline size={30} />
            <div className="provider-status-content">
              <strong>Proveedor Activo</strong>
              <span>Tu perfil profesional está público.</span>
            </div>
          </div>
          <button
            className="provider-action-btn"
            onClick={isMoroso ? handleRestrictedAccess : () => navigate('/registro-proveedor')}
          >
            Gestionar mi Perfil
          </button>
        </div>
      );
    }

    // Default: Not a provider
    return (
      <div className="profile-cards-group" style={{ textAlign: 'center', padding: '30px 24px' }}>
        <h3 style={{ marginTop: 0, fontSize: '1.2rem', color: '#1e293b' }}>¿Ofreces servicios?</h3>
        <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px' }}>Únete al marketplace y ofrece tus servicios a la comunidad.</p>
        <button className="provider-action-btn" onClick={() => navigate('/registro-proveedor')}>
          Quiero ser Proveedor
        </button>
      </div>
    );
  };

  return (
    <div className="ks-page">
      {/* ── DESKTOP SIDEBAR ─────────────────────── */}
      <KondoSidebar activeRoute="/perfil" />

      {/* ── MAIN AREA ────────────────────────────── */}
      <main className="ks-main">
        <div className="ks-scroll-area">
          <div className="profile-container">
            <div className="ks-profile-header-sticky-wrapper">
              <KondoBrandHeader showBack={false} />
            </div>
            {/* Premium Profile Header Banner */}
            <div className="profile-hero-section">
              <div className="profile-hero-center">
                <div className="avatar-wrapper" onClick={() => setShowAvatarModal(true)}>
                  <AvatarViewer avatarUrl={fotoPreview} size={40} />
                  <div className="camera-badge">
                    <IoCamera size={14} />
                  </div>
                </div>

                <button
                  type="button"
                  className="profile-customize-avatar-btn"
                  onClick={() => setShowAvatarModal(true)}
                >
                  Personalizar Avatar
                </button>

                <h1 className="profile-name-display">{userData?.nombre || 'Usuario'}</h1>
                <span className="profile-role-badge">
                  {userData?.tipoResidente && userData.tipoResidente !== 'Por definir'
                    ? userData.tipoResidente
                    : (userData?.rol === 'proveedor'
                      ? 'Proveedor'
                      : userData?.rol === 'admin'
                        ? 'Administrador'
                        : userData?.rol === 'comite'
                          ? 'Comité'
                          : userData?.rol === 'vigilante'
                            ? 'Seguridad'
                            : userData?.rol === 'superuser' || userData?.rol === 'masteruser'
                              ? 'Superusuario'
                              : 'Cargando...')}
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="profile-body-wrapper">
              <input type="file" id="foto-upload" accept="image/*" onChange={handleFotoChange} style={{ display: 'none' }} />

              {mensajeExito && <div className="profile-msg success">{mensajeExito}</div>}
              {error && <div className="profile-msg error">{error}</div>}

              {/* Info Personal */}
              <div className="profile-section-title">
                <span>Información Personal</span>
                <button type="button" onClick={() => document.getElementById('btn-guardar-perfil').click()} className="profile-section-edit">Guardar</button>
              </div>

              <div className="profile-cards-group">
                <div className="profile-data-card">
                  <span className="profile-data-label">Nombre Completo</span>
                  <div className="profile-data-value">
                    <input type="text" className="profile-data-input" value={nombre} onChange={e => setNombre(e.target.value)} required />
                  </div>
                </div>
                <div className="profile-data-card">
                  <span className="profile-data-label">Correo Electrónico</span>
                  <div className="profile-data-value">
                    <input type="email" className="profile-data-input" value={currentUser?.email} disabled />
                  </div>
                </div>
                <div className="profile-data-card">
                  <span className="profile-data-label">Teléfono</span>
                  <div className="profile-data-value">
                    <input type="tel" className="profile-data-input" value={numeroCelular} onChange={e => setNumeroCelular(e.target.value)} required />
                    <IoCheckmarkCircleOutline size={20} color="#16a34a" />
                  </div>
                </div>
              </div>

              {/* Info Condominio */}
              <div className="profile-section-title">
                <span>Información de Residencia</span>
              </div>

              <div className="profile-cards-group" style={{ padding: '0px' }}>
                <div className="profile-data-card" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div className="profile-minicard-icon" style={{ background: '#f5f3ff', color: '#7B61FF', width: '44px', height: '44px', margin: 0, flexShrink: 0 }}>
                      <IoHomeOutline size={22} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <span className="profile-data-label">Condominio</span>
                      <div className="profile-data-value">
                        <input type="text" className="profile-data-input" value={condominioNombre} disabled />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="profile-minicard-grid">
                <div className="profile-minicard">
                  <span className="profile-data-label">Unidad</span>
                  <div className="profile-data-value">
                    <select value={numeroCasa} onChange={(e) => setNumeroCasa(e.target.value)} className="profile-data-input" required disabled={true}>
                      <option value="" disabled>Selecciona</option>
                      {userData.condominioId ? unidadesLista.map(unidad => <option key={unidad} value={unidad}>{unidad}</option>) : <option value="N/A">N/A</option>}
                    </select>
                  </div>
                </div>
                <div className="profile-minicard">
                  <span className="profile-data-label">Relación</span>
                  <div className="profile-data-value">
                    <select value={tipoResidente} onChange={(e) => setTipoResidente(e.target.value)} className="profile-data-input" disabled={true}>
                      <option value="Inquilino">Inquilino</option>
                      <option value="Propietario">Propietario</option>
                      <option value="Por definir">En Revisión...</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Botón oculto real de submit */}
              <button type="submit" id="btn-guardar-perfil" style={{ display: 'none' }} disabled={loading}></button>

              {/* INQUILINOS CARD */}
              <GestionInquilinosCard
                currentUser={currentUser}
                userData={userData}
                codigoCondominio={codigoActivacion}
              />

              {/* Horario de Atención (Solo Admin/Comite) */}
              {(userData?.rol === 'admin' || userData?.rol === 'comite' || userData?.rol === 'superuser') && (
                <>
                  <div className="profile-section-title" style={{ marginTop: '28px' }}>
                    <span>Horario de Atención y Chat</span>
                  </div>
                  <div className="profile-cards-group" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ paddingRight: '10px' }}>
                        <strong style={{ display: 'block', fontSize: '0.95rem', color: '#0f172a', marginBottom: '4px', textAlign: 'left' }}>Limitar Horario de Atención</strong>
                        <span style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', lineHeight: '1.4', textAlign: 'left' }}>Fuera de horario no recibirás notificaciones de mensajes ni reportes.</span>
                      </div>
                      <div
                        onClick={() => setScheduleEnabled(!scheduleEnabled)}
                        style={{
                          width: '50px',
                          height: '28px',
                          borderRadius: '15px',
                          background: scheduleEnabled ? '#7B61FF' : '#cbd5e1',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          flexShrink: 0
                        }}
                      >
                        <div
                          style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            background: 'white',
                            position: 'absolute',
                            top: '3px',
                            left: scheduleEnabled ? '25px' : '3px',
                            transition: 'left 0.2s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        />
                      </div>
                    </div>

                    {scheduleEnabled && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div>
                            <span className="profile-data-label">Hora Inicio</span>
                            <input
                              type="time"
                              className="profile-data-input"
                              style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 12px', fontSize: '0.95rem', boxSizing: 'border-box' }}
                              value={scheduleStart}
                              onChange={(e) => setScheduleStart(e.target.value)}
                            />
                          </div>
                          <div>
                            <span className="profile-data-label">Hora Fin</span>
                            <input
                              type="time"
                              className="profile-data-input"
                              style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 12px', fontSize: '0.95rem', boxSizing: 'border-box' }}
                              value={scheduleEnd}
                              onChange={(e) => setScheduleEnd(e.target.value)}
                            />
                          </div>
                        </div>

                        <div>
                          <span className="profile-data-label" style={{ display: 'block', marginBottom: '8px' }}>Días de Atención</span>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {[
                              { label: 'L', value: 1 },
                              { label: 'M', value: 2 },
                              { label: 'M', value: 3 },
                              { label: 'J', value: 4 },
                              { label: 'V', value: 5 },
                              { label: 'S', value: 6 },
                              { label: 'D', value: 7 },
                            ].map((day) => {
                              const isActive = scheduleDays.includes(day.value);
                              return (
                                <button
                                  type="button"
                                  key={day.value}
                                  onClick={() => {
                                    if (isActive) {
                                      setScheduleDays(scheduleDays.filter((d) => d !== day.value));
                                    } else {
                                      setScheduleDays([...scheduleDays, day.value].sort());
                                    }
                                  }}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: isActive ? '#7B61FF' : '#f1f5f9',
                                    color: isActive ? 'white' : '#64748b',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    fontSize: '0.85rem',
                                  }}
                                >
                                  {day.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                          <div style={{ paddingRight: '10px' }}>
                            <strong style={{ display: 'block', fontSize: '0.95rem', color: '#0f172a', marginBottom: '4px', textAlign: 'left' }}>Auto-respuesta Activa</strong>
                            <span style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', lineHeight: '1.4', textAlign: 'left' }}>Envía una auto-respuesta por chat de forma automática fuera de horario.</span>
                          </div>
                          <div
                            onClick={() => setAutoReplyEnabled(!autoReplyEnabled)}
                            style={{
                              width: '50px',
                              height: '28px',
                              borderRadius: '15px',
                              background: autoReplyEnabled ? '#7B61FF' : '#cbd5e1',
                              position: 'relative',
                              cursor: 'pointer',
                              transition: 'background 0.2s',
                              flexShrink: 0
                            }}
                          >
                            <div
                              style={{
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                background: 'white',
                                position: 'absolute',
                                top: '3px',
                                left: autoReplyEnabled ? '25px' : '3px',
                                transition: 'left 0.2s',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              }}
                            />
                          </div>
                        </div>

                        {autoReplyEnabled && (
                          <div>
                            <span className="profile-data-label">Mensaje de Auto-respuesta</span>
                            <textarea
                              rows={3}
                              className="profile-data-input"
                              style={{
                                border: '1px solid #cbd5e1',
                                borderRadius: '12px',
                                padding: '12px 16px',
                                fontSize: '0.95rem',
                                marginTop: '6px',
                                width: '100%',
                                boxSizing: 'border-box',
                                fontFamily: 'inherit',
                              }}
                              placeholder="Escribe tu mensaje..."
                              value={autoReplyMessage}
                              onChange={(e) => setAutoReplyMessage(e.target.value)}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Extras */}
              {renderProviderSection()}

              <div className="profile-action-group">
                <div className="profile-secondary-btn" onClick={() => navigate('/datos-fiscales')}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IoReceiptOutline style={{ marginRight: 12, fontSize: '1.2rem', color: '#64748b' }} /> Datos de Facturación
                  </div>
                  <span style={{ color: '#cbd5e1' }}>{'>'}</span>
                </div>

                <div className="profile-secondary-btn" onClick={() => navigate('/metodos-pago')}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IoCardOutline style={{ marginRight: 12, fontSize: '1.2rem', color: '#64748b' }} /> Métodos de Pago
                  </div>
                  <span style={{ color: '#cbd5e1' }}>{'>'}</span>
                </div>

                <div className="profile-secondary-btn" onClick={handleContactAdmin}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IoChatbubblesOutline style={{ marginRight: 12, fontSize: '1.2rem', color: '#64748b' }} /> Chatear con Administrador
                  </div>
                  <span style={{ color: '#cbd5e1' }}>{'>'}</span>
                </div>

                 <div className="profile-secondary-btn" onClick={handleChangePasswordClick}>
                   <div style={{ display: 'flex', alignItems: 'center' }}>
                     <IoShieldCheckmarkOutline style={{ marginRight: 12, fontSize: '1.2rem', color: '#64748b' }} /> Seguridad de la Cuenta
                   </div>
                   <span style={{ color: '#cbd5e1' }}>{'>'}</span>
                 </div>

                <div className="profile-secondary-btn" onClick={() => { setLegalModalType('terms'); setShowLegalModal(true); }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IoShieldCheckmarkOutline style={{ marginRight: 12, fontSize: '1.2rem', color: '#64748b' }} /> Términos y Condiciones
                  </div>
                  <span style={{ color: '#cbd5e1' }}>{'>'}</span>
                </div>

                <div className="profile-secondary-btn" onClick={() => { setLegalModalType('privacy'); setShowLegalModal(true); }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IoDocumentTextOutline style={{ marginRight: 12, fontSize: '1.2rem', color: '#64748b' }} /> Aviso de Privacidad
                  </div>
                  <span style={{ color: '#cbd5e1' }}>{'>'}</span>
                </div>

                {(userData?.rol === 'comite' || userData?.rol === 'superuser' || userData?.rol === 'masteruser') && (
                  <div className="profile-secondary-btn" onClick={() => navigate('/configuracion-condominio')}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <IoConstructOutline style={{ marginRight: 12, fontSize: '1.2rem', color: '#64748b' }} /> Panel de Comité
                    </div>
                    <span style={{ color: '#cbd5e1' }}>{'>'}</span>
                  </div>
                )}
              </div>

              <div className="profile-action-group">
                <div className="profile-secondary-btn danger" onClick={handleLogout}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IoLogOutOutline style={{ marginRight: 12, fontSize: '1.2rem' }} /> Cerrar Sesión
                  </div>
                </div>
              </div>

              <div className="profile-action-group" style={{ marginTop: '10px' }}>
                <div className="profile-secondary-btn danger" onClick={handleDeleteAccount} style={{ background: '#fef2f2', borderColor: '#fee2e2' }}>
                  <div style={{ display: 'flex', alignItems: 'center', color: '#dc2626' }}>
                    <IoCloseCircleOutline style={{ marginRight: 12, fontSize: '1.2rem', color: '#dc2626' }} /> Eliminar mi Cuenta
                  </div>
                </div>
              </div>

            </form>

            <div style={{ textAlign: 'center', marginTop: 20, color: '#9ca3af', fontSize: '0.8rem' }}>
              v{APP_VERSION}
            </div>
          </div>
        </div>

        {/* ── FLOATING ISLAND BOTTOM NAV (mobile) ── */}
        <KondoBottomNav activeRoute="/perfil" />

        {/* Modal para Seguridad (Contraseña y PIN) */}
        {showPasswordModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }} onClick={() => { setShowPasswordModal(false); setError(''); setMensajeExito(''); }}>
            <div style={{ background: '#fff', borderRadius: '16px', padding: '25px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '1.2rem', margin: 0, color: '#1e293b', fontWeight: 'bold' }}>Seguridad de la Cuenta</h2>
                <button type="button" onClick={() => { setShowPasswordModal(false); setError(''); setMensajeExito(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                  <IoCloseCircleOutline size={26} />
                </button>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', borderBottom: '2px solid #f1f5f9', marginBottom: '20px' }}>
                <button
                  type="button"
                  onClick={() => { setActiveSecurityTab('password'); setError(''); setMensajeExito(''); }}
                  style={{
                    flex: 1,
                    padding: '10px 5px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeSecurityTab === 'password' ? '2px solid #6366f1' : 'none',
                    color: activeSecurityTab === 'password' ? '#6366f1' : '#64748b',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    outline: 'none',
                    marginBottom: '-2px'
                  }}
                >
                  Contraseña
                </button>
                <button
                  type="button"
                  onClick={() => { setActiveSecurityTab('pin'); setError(''); setMensajeExito(''); }}
                  style={{
                    flex: 1,
                    padding: '10px 5px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeSecurityTab === 'pin' ? '2px solid #6366f1' : 'none',
                    color: activeSecurityTab === 'pin' ? '#6366f1' : '#64748b',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    outline: 'none',
                    marginBottom: '-2px'
                  }}
                >
                  PIN de Seguridad
                </button>
              </div>

              {/* Mensajes de Alerta Internos del Modal */}
              {error && (
                <div style={{ color: '#dc2626', background: '#fef2f2', border: '1px solid #fee2e2', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '15px', fontWeight: '500', textAlign: 'center' }}>
                  {error}
                </div>
              )}
              {mensajeExito && (
                <div style={{ color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '15px', fontWeight: '500', textAlign: 'center' }}>
                  {mensajeExito}
                </div>
              )}

              {activeSecurityTab === 'password' ? (
                <form onSubmit={handleConfirmPasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div className="profile-form-group" style={{ margin: 0 }}>
                    <label className="profile-label" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#475569' }}>Contraseña Actual</label>
                    <input
                      type="password"
                      className="profile-input"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Ingresa tu contraseña actual"
                      required
                      style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="profile-form-group" style={{ margin: 0 }}>
                    <label className="profile-label" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#475569' }}>Nueva Contraseña</label>
                    <input
                      type="password"
                      className="profile-input"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      required
                      minLength={6}
                      style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="profile-form-group" style={{ margin: 0 }}>
                    <label className="profile-label" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#475569' }}>Confirmar Contraseña</label>
                    <input
                      type="password"
                      className="profile-input"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repite tu nueva contraseña"
                      required
                      minLength={6}
                      style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <button type="submit" className="profile-save-btn" disabled={loading} style={{ marginTop: '10px', padding: '12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                    {loading ? 'Procesando...' : 'Actualizar Contraseña'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleUpdatePin} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {userData?.securityPin && (
                    <div className="profile-form-group" style={{ margin: 0 }}>
                      <label className="profile-label" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#475569' }}>PIN Actual</label>
                      <input
                        type="text"
                        pattern="[0-9]*"
                        inputMode="numeric"
                        maxLength={4}
                        className="profile-input"
                        value={currentPin}
                        onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="Ingresa tu PIN actual"
                        required
                        style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1.2rem', textAlign: 'center', letterSpacing: '8px', fontWeight: 'bold', width: '100%', boxSizing: 'border-box' }}
                      />
                    </div>
                  )}
                  <div className="profile-form-group" style={{ margin: 0 }}>
                    <label className="profile-label" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#475569' }}>{userData?.securityPin ? 'Nuevo PIN (4 dígitos)' : 'PIN de Desbloqueo (4 dígitos)'}</label>
                    <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 8px' }}>Se utiliza para bloquear/desbloquear la aplicación de forma rápida y segura desde tu cuenta.</p>
                    <input
                      type="text"
                      pattern="[0-9]*"
                      inputMode="numeric"
                      maxLength={4}
                      className="profile-input"
                      value={securityPin}
                      onChange={(e) => setSecurityPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="Ej. 1234"
                      required
                      style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1.2rem', textAlign: 'center', letterSpacing: '8px', fontWeight: 'bold', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <button type="submit" className="profile-save-btn" disabled={loading} style={{ marginTop: '10px', padding: '12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                    {loading ? 'Procesando...' : 'Actualizar PIN de Seguridad'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Modal para Seleccionar Avatar / Personalizar */}
        {showAvatarModal && (
          <div className="avatar-modal-overlay" onClick={() => setShowAvatarModal(false)}>
            <div className="avatar-modal-content" onClick={e => e.stopPropagation()}>
              <div className="avatar-modal-header">
                <h2>Personalizar Perfil</h2>
                <button type="button" className="avatar-modal-close" onClick={() => setShowAvatarModal(false)}>
                  <IoCloseCircleOutline size={28} />
                </button>
              </div>

              {/* Tabs */}
              <div className="avatar-modal-tabs">
                <button
                  type="button"
                  className={`avatar-modal-tab ${activeTab === 'brand' ? 'active' : ''}`}
                  onClick={() => setActiveTab('brand')}
                >
                  Marca Kondify
                </button>
                <button
                  type="button"
                  className={`avatar-modal-tab ${activeTab === 'emoji' ? 'active' : ''}`}
                  onClick={() => setActiveTab('emoji')}
                >
                  Emojis Premium
                </button>
              </div>

              <div className="avatar-modal-body">
                {activeTab === 'brand' && (
                  <div className="avatar-presets-grid">
                    {/* Custom Upload Option first */}
                    <label htmlFor="foto-upload-modal" className="avatar-preset-item upload-box">
                      <IoCamera size={26} />
                      <span>Subir Foto</span>
                      <input
                        type="file"
                        id="foto-upload-modal"
                        accept="image/*"
                        onChange={handleFotoChange}
                        style={{ display: 'none' }}
                      />
                    </label>

                    {BRAND_AVATARS.map(brand => (
                      <div
                        key={brand.id}
                        className={`avatar-preset-item brand-item ${fotoPreview === `brand:${brand.id}` ? 'selected' : ''}`}
                        onClick={() => handleSelectPresetAvatar(`brand:${brand.id}`)}
                      >
                        <img src={brand.src} alt={brand.name} className="avatar-preset-brand-img" />
                        <span>{brand.name}</span>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'emoji' && (
                  <div className="avatar-presets-grid">
                    {EMOJI_AVATARS.map((item, idx) => {
                      const presetString = `emoji:${item.emoji}:${item.gradient}`;
                      const isSelected = fotoPreview === presetString;
                      return (
                        <div
                          key={idx}
                          className={`avatar-preset-item emoji-item ${isSelected ? 'selected' : ''}`}
                          style={{ background: item.gradient }}
                          onClick={() => handleSelectPresetAvatar(presetString)}
                        >
                          <span className="avatar-preset-emoji">{item.emoji}</span>
                          <span className="avatar-preset-label">{item.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showLegalModal && (
          <div className="modal-overlay" onClick={() => setShowLegalModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%', height: '80vh', display: 'flex', flexDirection: 'column', padding: '24px', borderRadius: '20px' }}>
              <button className="modal-close-btn" onClick={() => setShowLegalModal(false)} style={{ top: '16px', right: '16px' }}>
                <IoCloseCircleOutline size={26} color="#64748b" />
              </button>
              <h2 style={{ fontSize: '1.3rem', marginBottom: '16px', color: '#0f172a', fontWeight: '800', textAlign: 'left', fontFamily: 'Manrope, sans-serif' }}>
                {legalModalType === 'terms' ? 'Términos y Condiciones de Uso' : 'Aviso de Privacidad'}
              </h2>
              <div style={{ flex: 1, overflowY: 'auto', textAlign: 'left', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '14px', border: '1px solid #e2e8f0', margin: '0 0 10px 0' }}>
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.85rem', color: '#334155', margin: 0, lineHeight: '1.5' }}>
                  {legalModalType === 'terms' ? TERMINOS_CONDICIONES_TEXT : AVISO_PRIVACIDAD_TEXT}
                </pre>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default PerfilScreen;
